<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div v-if="loading" class="text-center p-3">
    <i class="ri-loader-4-line ri-spin text-4xl"></i>
  </div>
  <div v-else-if="!hasGroupedSearches" class="text-center text-gray-500 p-3">
    {{ $t("vuesFavorites.noSavedViews") }}
  </div>
  <div v-else>
    <div class="flex flex-column gap-4">
      <div
        v-if="!globalOnly && personalViews.length > 0"
        class="flex flex-column gap-2"
      >
        <label for="personal-searches-dropdown">{{
          $t("vuesFavorites.personalViews")
        }}</label>
        <PrimeSelect
          id="personal-searches-dropdown"
          v-model="selectedPersonalSearch"
          :options="personalViews"
          optionLabel="name"
          :placeholder="$t('vuesFavorites.selectPersonalView')"
          @change="onDropdownChange"
          filter
          :filterBy="['name', 'description']"
          :filterPlaceholder="$t('vuesFavorites.filterSavedViews')"
        >
          <template #option="slotProps">
            <div
              class="flex flex-column align-items-start w-30rem overflow-hidden"
            >
              <div class="font-bold">{{ slotProps.option.name }}</div>
              <small
                v-if="slotProps.option.description"
                class="text-color-secondary w-full overflow-hidden text-overflow-ellipsis white-space-nowrap block"
                v-tooltip.left="slotProps.option.description"
              >
                {{ slotProps.option.description }}
              </small>
            </div>
          </template>
        </PrimeSelect>
      </div>
      <div v-if="globalViews.length > 0" class="flex flex-column gap-2">
        <label for="global-searches-dropdown">{{
          $t("vuesFavorites.globalViews")
        }}</label>
        <PrimeSelect
          id="global-searches-dropdown"
          v-model="selectedGlobalSearch"
          :options="globalViews"
          optionLabel="name"
          :placeholder="$t('vuesFavorites.selectGlobalView')"
          @change="onDropdownChange"
          filter
          :filterBy="['name', 'description']"
          :filterPlaceholder="$t('vuesFavorites.filterSavedViews')"
        >
          <template #option="slotProps">
            <div
              class="flex flex-column align-items-start w-30rem overflow-hidden"
            >
              <div class="font-bold">{{ slotProps.option.name }}</div>
              <small
                v-if="slotProps.option.description"
                class="text-color-secondary w-full overflow-hidden text-overflow-ellipsis white-space-nowrap block"
                v-tooltip.left="slotProps.option.description"
              >
                {{ slotProps.option.description }}
              </small>
            </div>
          </template>
        </PrimeSelect>
      </div>
    </div>
  </div>
</template>

<script>
import Select from "primevue/select";
import { mapState, mapActions } from "pinia";
import { useUserViewStore } from "@/stores/userView.js";

export default {
  name: "UserViewsFavoritesPanel",
  components: {
    PrimeSelect: Select,
  },
  props: {
    globalOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPersonalSearch: null,
      selectedGlobalSearch: null,
    };
  },
  mounted() {
    this.fetchSavedViews();
  },
  computed: {
    ...mapState(useUserViewStore, {
      loadedView: "loadedView",
      personalViews: "personalViews",
      globalViews: "globalViews",
      loading: "viewsLoading",
    }),
    hasGroupedSearches() {
      if (this.globalOnly) {
        return this.globalViews.length > 0;
      }
      return this.personalViews.length > 0 || this.globalViews.length > 0;
    },
  },
  emits: ["load-view"],
  methods: {
    ...mapActions(useUserViewStore, ["fetchSavedViews"]),
    onDropdownChange(event) {
      if (event.value) {
        this.selectView(event.value);
        // Reset dropdowns to act as action buttons
        this.$nextTick(() => {
          this.selectedPersonalSearch = null;
          this.selectedGlobalSearch = null;
        });
      }
    },
    selectView(search) {
      this.$emit("load-view", search);
    },
  },
};
</script>

<style scoped>
.multi-line-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
}
</style>
